import { notifications } from '@mantine/notifications';
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks';
import { updateUserSettings } from '@/services';
import { DEFAULT_FONT_SCALE_ID, FONT_SCALE_MULTIPLIERS } from '@/theme';
import type { FontScaleId } from '@/types';

export interface FontScaleContextValue {
  fontScaleId: FontScaleId;
  /** Switches the active font scale immediately and persists it for the signed-in user. */
  setFontScaleId: (id: FontScaleId) => void;
}

export const FontScaleContext = createContext<FontScaleContextValue | undefined>(undefined);

interface FontScaleProviderProps {
  children: ReactNode;
}

/**
 * Owns the active font-size scale: defaults to the signed-in user's saved
 * `fontScale` preference (falling back to `regular` for signed-out users or
 * accounts predating this feature), and applies it as a `--app-font-scale`
 * CSS custom property on the root element. `src/index.css` uses this to
 * scale the root `<html>` font-size, which — since Mantine's sizing is
 * `rem`-based — scales the whole app proportionally.
 */
export const FontScaleProvider = ({ children }: FontScaleProviderProps) => {
  const { appUser, firebaseUser } = useAuth();
  const { t } = useTranslation();
  const [fontScaleId, setFontScaleIdState] = useState<FontScaleId>(DEFAULT_FONT_SCALE_ID);

  // Adopt the signed-in user's saved preference once their profile loads
  // (or reset to default on sign-out). Local overrides via `setFontScaleId`
  // still take effect immediately without waiting on this.
  useEffect(() => {
    if (appUser?.fontScale && FONT_SCALE_MULTIPLIERS[appUser.fontScale]) {
      setFontScaleIdState(appUser.fontScale);
    } else if (!firebaseUser) {
      setFontScaleIdState(DEFAULT_FONT_SCALE_ID);
    }
  }, [appUser?.fontScale, firebaseUser]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--app-font-scale',
      String(FONT_SCALE_MULTIPLIERS[fontScaleId]),
    );
  }, [fontScaleId]);

  const setFontScaleId = useCallback(
    (id: FontScaleId) => {
      setFontScaleIdState(id);
      if (firebaseUser) {
        updateUserSettings(firebaseUser.uid, { fontScale: id }).catch(() => {
          notifications.show({ color: 'red', message: t('profile.errors.saveFailed') });
        });
      }
    },
    [firebaseUser, t],
  );

  const contextValue = useMemo<FontScaleContextValue>(
    () => ({ fontScaleId, setFontScaleId }),
    [fontScaleId, setFontScaleId],
  );

  return <FontScaleContext.Provider value={contextValue}>{children}</FontScaleContext.Provider>;
};
